import { useDeferredValue, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layers3, Plus, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import type { DashboardResponse, LinkListResponse, LinkStatus, ShortLink } from "../../shared/types";
import { api } from "../lib/api";
import { number } from "../lib/format";
import { useSession } from "../context/session";
import { PageHeader } from "../components/app-shell";
import { BatchCreateDialog } from "../components/batch-create-dialog";
import { CreateLinkPanel } from "../components/create-link-panel";
import { LinkTable } from "../components/link-table";
import { QrDialog } from "../components/qr-dialog";
import { Button, ErrorState, Loading } from "../components/ui";

const filters: Array<{ value: "all" | LinkStatus; label: string; en: string }> = [
  { value: "all", label: "全部", en: "all" },
  { value: "active", label: "生效", en: "active" },
  { value: "pending", label: "待审", en: "review" },
  { value: "grace", label: "宽限期", en: "grace" },
  { value: "released", label: "已释放", en: "released" },
];

export function LinksPage() {
  const { user, config } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(searchParams.has("create"));
  const [batchOpen, setBatchOpen] = useState(false);
  const [qr, setQr] = useState<ShortLink | null>(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [status, setStatus] = useState<"all" | LinkStatus>("all");
  const [page, setPage] = useState(1);

  const dashboardQuery = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<DashboardResponse>("/api/dashboard"),
  });
  const listQuery = useQuery({
    queryKey: ["links", page, status, deferredSearch],
    queryFn: () => {
      const query = new URLSearchParams({ page: String(page), pageSize: "20" });
      if (status !== "all") query.set("status", status);
      if (deferredSearch.trim()) query.set("q", deferredSearch.trim());
      return api<LinkListResponse>(`/api/links?${query.toString()}`);
    },
  });
  if (!user) return null;

  const closeCreate = () => {
    setCreateOpen(false);
    if (searchParams.has("create")) {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      next.delete("slug");
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <div className="page links-page">
      <PageHeader
        eyebrow="LINK INVENTORY · 实时目录"
        title={user.role === "user" ? "我的短链" : "短链总览"}
        body="搜索、创建并管理每一个短地址；状态、点击和生命周期在同一行里保持可见。"
        actions={
          <>
            <Button onClick={() => setBatchOpen(true)}><Layers3 size={15} />批量生成</Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} />新建短链</Button>
          </>
        }
      />
      <CreateLinkPanel
        user={user}
        userSlugLength={config?.userSlugLength ?? 6}
        vipSlugLength={config?.vipSlugLength ?? 5}
        allowUserCustomSlug={config?.allowUserCustomSlug ?? false}
        open={createOpen}
        onClose={closeCreate}
        initialSlug={searchParams.get("slug") ?? ""}
        onShowQr={setQr}
      />
      {dashboardQuery.isLoading ? <Loading label="读取概览" /> : dashboardQuery.error ? <ErrorState error={dashboardQuery.error} retry={() => void dashboardQuery.refetch()} /> : dashboardQuery.data ? (
        <section className="metric-grid" aria-label="关键指标">
          <div><span className="eyebrow">TOTAL LINKS</span><strong>{number(dashboardQuery.data.metrics.totalLinks)}</strong><small>短链总数</small></div>
          <div><span className="eyebrow">CLICKS TODAY</span><strong>{number(dashboardQuery.data.metrics.clicksToday)}</strong><small>今日点击</small></div>
          <div className="metric-grid__accent"><span className="eyebrow">IN REVIEW</span><strong>{number(dashboardQuery.data.metrics.pendingReview)}</strong><small>待审核</small></div>
          <div><span className="eyebrow">EXPIRING 7D</span><strong>{number(dashboardQuery.data.metrics.expiringSoon)}</strong><small>7 天内到期</small></div>
        </section>
      ) : null}
      <section className="list-section">
        <header className="list-toolbar">
          <div className="search-field"><Search size={15} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="搜索 slug、目标域名、创建人" aria-label="搜索短链" /></div>
          <div className="segments" role="group" aria-label="状态筛选">
            {filters.map((filter) => <button type="button" key={filter.value} className={status === filter.value ? "segment segment--active" : "segment"} onClick={() => { setStatus(filter.value); setPage(1); }}>{filter.label}<span>{filter.en}</span></button>)}
          </div>
          <span className="list-toolbar__count">{listQuery.data ? `${listQuery.data.pagination.total} ITEMS` : "—"}</span>
        </header>
        {listQuery.isLoading ? <Loading label="读取短链" /> : listQuery.error ? <ErrorState error={listQuery.error} retry={() => void listQuery.refetch()} /> : listQuery.data ? (
          <>
            <LinkTable links={listQuery.data.items} />
            <footer className="pagination">
              <span>第 {listQuery.data.pagination.page} / {listQuery.data.pagination.totalPages} 页 · 每页 20 条</span>
              <div><Button disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>← 上一页</Button><Button disabled={page >= listQuery.data.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>下一页 →</Button></div>
            </footer>
          </>
        ) : null}
      </section>
      <BatchCreateDialog open={batchOpen} onClose={() => setBatchOpen(false)} />
      <QrDialog link={qr} onClose={() => setQr(null)} />
    </div>
  );
}
